import { ReactElement, useEffect, useState } from "react";
import PortfolioService from "../../../../../services/PortfolioService";
import RatioTable from "../../../../../components/RatioTable/RatioTable";
import { Dropdown, IDropdownOption, Spinner, TooltipHost } from "@fluentui/react";
import { TButtonsTable, TDataWallet, TRatiosCarterasData, TRatiosCarterasDBModel } from "../../types/CarteraRatioTypes";
import { dropdownStylesMultiple } from "../../Styles/DropDownStyles";
import { MapRatiosToGraph, TDataSeries } from "../../Util/MapRatiosToGraph";
import HighchartsReactCustom, { TGraphDataChart } from "../../../../../components/HighChartsReactCustom/HighChartsReactCustom";
import { useBoolean } from "@fluentui/react-hooks";
import { tipImages, tipTexts } from "../../TooltipData/TooltipData";
import CustomTeachingBubble from "../../../../../components/CustomTeachingBubble/CustomTeachingBubble";
import { buttonsGraphList, buttonsTableList } from "../../Util/texts";
import { TActivePlan } from "../../types/TActivePlan";
import { TableData } from "../../Util/TableData";
import { TRatiosIndicesDBModel } from "../../types/IndicesTypes";
import { FilterableDropdown, TSelectorOption } from "../../../../../components/FilterableDropdown/FilterableDropdown";

const BASE_CLASS = "analisis_cuantitativo";

type TAnalisisCuantitativoProps = {
    activePlan: TActivePlan;
    options: IDropdownOption[];
}

const AnalisisCuantitativo = ({ activePlan, options } : TAnalisisCuantitativoProps ) => {

    const [tooltipRatio, { setTrue: showTooltipRatio, setFalse: hideTooltipRatio }] = useBoolean(false);
    const [tablePeriod, setTablePeriod] = useState<string>('Inception');

    // SELECTED
    const [selectedWallets, setSelectedWallets] = useState<IDropdownOption[]>([]);

    // SAVED
    const [walletsSaved, setWalletsSaved] = useState<TRatiosCarterasData[]>([]);
    const [graphRatiosSaved, setGraphRatiosSaved] = useState<TGraphDataChart[]>([]);

    const [dataTable, setDataTable] = useState<TDataWallet[]>([]);

    // STATE BUTTONS GRAPHS
    const [graphRatio, setGraphRatio] = useState<TButtonsTable>({text: 'Retorno anualizado', value: 'linear_return'});
    const [secondGraphRatio, setSecondGraphRatio] = useState<TButtonsTable>({text: 'Volatilidad', value: 'volatility'});

    // STATE DATA GRAPHS
    const [graphRatioData, setGraphRatioData] = useState<TGraphDataChart[]>([]);
    const [secondGraphRatioData, setSecondGraphRatioData] = useState<TGraphDataChart[]>([]);
    const [walletsData, setWalletsData] = useState<TRatiosCarterasData[]>([]);

    const [isloadingRatioCartera, setIsLoadingRatioCartera] = useState<boolean>(true);
    const [isloadingGraph, setIsloadingGraph] = useState<boolean>(true);
    const [isloadingSecondGraph, setIsloadingSecondGraph] = useState<boolean>(true);

    // INDICES
    const [indexOptions, setIndexOptions] = useState<IDropdownOption[]>();
    const [indexOptionsSelected, setIndexOptionsSelected] = useState<IDropdownOption[]>([]);
    const [disableInception, setDisableInception] = useState<boolean>(false);

    useEffect(() => {
        updateGraphs({ graphRatioSelected: graphRatio, 
            setState: setGraphRatioData, setLoader: setIsloadingGraph });
        updateGraphs({ graphRatioSelected: secondGraphRatio, 
            setState: setSecondGraphRatioData, setLoader: setIsloadingSecondGraph });
    }, [selectedWallets, tablePeriod, graphRatio, secondGraphRatio]);

    useEffect(() => {
        setDataTable(TableData({ tablePeriod: tablePeriod, ratios: walletsData }))
    }, [tablePeriod, walletsData]);

    useEffect(() => {
        setDisableInception(indexOptionsSelected.length > 0)
        setTablePeriod(indexOptionsSelected.length > 0 ? "1Y" : "Inception");
    }, [indexOptionsSelected])
    
    useEffect(() => {
        updateWallets();
    }, [selectedWallets])

    useEffect(() => {
        getRatiosCarteras({name: activePlan.name, contrato: activePlan.finametrixContract})
            .then((result: TRatiosCarterasData) => {
                setWalletsData([result]);
                setWalletsSaved([result]);
                setIsLoadingRatioCartera(false);
                setSelectedWallets([{
                    key: result.data[0].nomcontrato,
                    text: result.name,
                    id: activePlan.id.toString()
                }]);
            }
        );
        getAllIndices();
    }, []);

    const getAllIndices = async () => {
        const allIndexResult = await PortfolioService.GetAllIndicesYModelos();

        const indexOpt: IDropdownOption[] = allIndexResult
            .map(index => ({
                key: index.finametrixId,
                text: index.nombreGenerico,
                id: index.finametrixId
            }));
        setIndexOptions(indexOpt);
    };

    const getRatioIndiceByRatioPeriod = async ({ratio, finametrixId, walletName}: {ratio: string, finametrixId: string, walletName: string}): Promise<TGraphDataChart> => {
        try {
            const result = await PortfolioService.GetRatiosIndicesByFinametrixIdRatioPeriod({ratio: ratio, period: tablePeriod, finametrixId: finametrixId})
            const resultMapped = result.map<TRatiosCarterasDBModel>((ratioIndice: TRatiosIndicesDBModel) => ({
                nomcontrato: ratioIndice.finametrixId,
                dateCalculation: ratioIndice.dateCalculation,
                period: ratioIndice.period,
                ratio: ratioIndice.ratio,
                valueCartera: ratioIndice.valueCartera
            }));
            const dataGraph = MapRatiosToGraph({ratios: resultMapped});
            const graphDataWithName: TGraphDataChart = { walletName: walletName, ratio: ratio, contrato: finametrixId, period: tablePeriod, data: dataGraph };
            return graphDataWithName;
        } catch(error) {
            console.error(error)
            const dataEmpty = [] as TDataSeries[]
            const graphDataWithNameEmpty: TGraphDataChart = {walletName: walletName, ratio: ratio, contrato: finametrixId, period: tablePeriod, data: dataEmpty };
            return graphDataWithNameEmpty;
            }
    };

    const onChangeIndice = async (option: TSelectorOption | undefined): Promise<void> => {
        setIsLoadingRatioCartera(true);
        if (!option) {
            console.warn("Option is undefined");
            return;
        } else if (option.id) {
                const indexResult = await PortfolioService.GetRatiosIndicesByFinametrixId(option?.id);

                const mapResult: TRatiosCarterasData = {
                    name: option.text,
                    data: indexResult.map((index: TRatiosIndicesDBModel) => ({
                        nomcontrato: index.finametrixId,
                        dateCalculation: index.dateCalculation,
                        period: index.period,
                        ratio: index.ratio,
                        valueCartera: index.valueCartera
                    }))
                };

                setWalletsSaved(prevState => [...prevState, mapResult])
                setWalletsData(prevState => [...prevState, mapResult]);

                setIndexOptionsSelected(prevState => 
                    option.selected
                        ? [...prevState, option]
                        : prevState.filter(item => item.key !== option.key)
                );

                setSelectedWallets(prevState =>
                    option.selected
                        ? [...prevState, option]
                        : prevState.filter(item => item.key !== option.key)
                );
        }
        setIsLoadingRatioCartera(false);
    };
    
    const getRatiosCarteras = async ({name, contrato}:{name: string, contrato: string}): Promise<TRatiosCarterasData> => {
        const result = await PortfolioService.getRatioCarteraByNomcontrato(contrato)
        const ratiosData: TRatiosCarterasData = {name: name, data: result};
        return ratiosData;   
    };

    const getRatioFromPeriod = async ({ratio, contrato, walletName}: {ratio: string, contrato: string, walletName: string}): Promise<TGraphDataChart> => {
        try {
            const result = await PortfolioService.getRatioCarteraByRatioAndPeriodAndNomcontrato({ratio: ratio, period: tablePeriod, nomcontrato: contrato})
            const dataGraph = MapRatiosToGraph({ratios: result});
            const graphDataWithName: TGraphDataChart = { walletName: walletName, ratio: ratio, contrato: contrato, period: tablePeriod, data: dataGraph };
            return graphDataWithName;
        } catch(error) {
            console.error(error)
            const dataEmpty = [] as TDataSeries[]
            const graphDataWithNameEmpty: TGraphDataChart = {walletName: walletName, ratio: ratio, contrato: contrato, period: tablePeriod, data: dataEmpty };
            return graphDataWithNameEmpty;
            }
    };

    const handleRenderTitle = (items: IDropdownOption[] | undefined): ReactElement<any, any> => {
        const dropDownTitle = items?.length === 1 ? items[0].text : "Cartera resumen"
        return <>{dropDownTitle}</>;
    };

    const onUpdateSelectionWallets = async (option: IDropdownOption | undefined): Promise<void> => {
        setIsLoadingRatioCartera(true);
        if (!option) {
            console.warn("Option is undefined");
            return;
        }

        setSelectedWallets(prevState =>
            option.selected
                ? [...prevState, option]
                : prevState.filter(item => item.key !== option.key)
        );
    };

    const updateWallets = async () => {
        const walletsDataFiltered = selectedWallets.map(async (selection: IDropdownOption) => {
            const walletExist = 
                walletsData.find((wallet: TRatiosCarterasData) => wallet.name === selection.text) ||
                walletsSaved.find((ratioWallet: TRatiosCarterasData) => ratioWallet.name === selection.text);

            return walletExist ?? getRatiosCarteras({ name: selection.text, contrato: selection.key.toString() })
                .then(result => {
                    setWalletsSaved(prevState =>
                        prevState.some(item => item.name === result.name) ? prevState : [...prevState, result]
                    );
        
                    return result;
                })
                .catch (err => {
                    console.error(err);
                    return null;
                }) 
        });

        setWalletsData((await Promise.all(walletsDataFiltered))
            .filter((item): item is TRatiosCarterasData => { 
                setIsLoadingRatioCartera(false);
                return item !== null;
            }));
    };

    const updateGraphs = async ({
        graphRatioSelected,
        setState,
        setLoader
    }: {
        graphRatioSelected: TButtonsTable,
        setState: Function,
        setLoader: Function
    }) => {
        setLoader(true);

        const fetchGraphData = async (selection: IDropdownOption): Promise<TGraphDataChart | null> => {
            const { text: walletName, key } = selection;
            const contrato = key.toString();
            const ratioValue = graphRatioSelected.value;

            const foundGraphSaved = graphRatiosSaved?.find(graphData =>
                graphData.walletName === walletName &&
                graphData.ratio === ratioValue &&
                graphData.period === tablePeriod
            );

            if (foundGraphSaved) return foundGraphSaved;

            const ratio = await getRatioFromPeriod({ ratio: ratioValue, walletName, contrato });
            if (ratio && ratio.data?.length !== undefined && ratio.data.length > 0) {
                setGraphRatiosSaved(prev => [...prev, ratio]);
                return ratio;
            } else {
                const ratioIndice = await getRatioIndiceByRatioPeriod({ ratio: ratioValue, walletName, finametrixId: contrato });
                setGraphRatiosSaved(prev => [...prev, ratioIndice]);
                return ratioIndice;
            }
        };

        setState([]);
        const graphsData = await Promise.all(selectedWallets.map(fetchGraphData));
        setState(graphsData.filter((item): item is TGraphDataChart => item !== null));
        setLoader(false);
    };
        
    return (
        <div className={`${BASE_CLASS}__body__container`}>
            <div className={`${BASE_CLASS}__left__container`}>
                <div className={`${BASE_CLASS}__dropdown__container`}>
                    <Dropdown placeholder={activePlan.name}
                        className={`${BASE_CLASS}__dropdown`}
                        options={options as IDropdownOption[]}
                        defaultSelectedKeys={[activePlan.finametrixContract]}
                        onRenderTitle={handleRenderTitle}
                        onChange={(_, option) => onUpdateSelectionWallets(option)}
                        styles={dropdownStylesMultiple}
                        multiSelect
                    />
                </div>
                
                <RatioTable titleCard="Ratios Cartera" ratios={walletsData} resultTable={dataTable} isLoading={isloadingRatioCartera} /> 
            </div>
            <div className={`${BASE_CLASS}__right__container`}>               
                <div id="table_perdiod_id" className={`${BASE_CLASS}__buttons__period`}>
                    
                    {buttonsTableList.map((buttonTable: TButtonsTable) => (
                        <button
                            disabled={buttonTable.text === "Inicio" && disableInception} 
                            className={`${tablePeriod === buttonTable.value ? 'active' : ''} fileButtons`} 
                            onClick={() => {
                                setTablePeriod(buttonTable.value)
                            }}>{buttonTable.text}</button>
                    ))}
                    <img id="tip_location" onMouseEnter={showTooltipRatio} onClick={showTooltipRatio} style={{ verticalAlign: 'bottom' }} src="/icons/informationBlack.svg" alt="Información" />
                    {tooltipRatio && <CustomTeachingBubble texts={tipTexts} images={tipImages} targetIdentifier="tip_location" showTooltip={hideTooltipRatio} />}
                </div>
                <div className={`${BASE_CLASS}__graph__container`}>
                    <div className={`${BASE_CLASS}__indices__container`}>
                        {indexOptions && <FilterableDropdown allOptions={indexOptions as TSelectorOption[]} onChangeIndex={onChangeIndice} />}
                    </div>    
                    {isloadingGraph && <Spinner size={3} style={{ padding: "1em" }} />}
                    <div className={`${BASE_CLASS}__graph__card`}>
                        <div className={`${BASE_CLASS}__buttons__graph`}>
                            {buttonsGraphList.map((buttonTable: TButtonsTable) => (
                                <TooltipHost content={buttonTable.toolTipHostContent}>
                                    <button className={`${graphRatio.value === buttonTable.value ? 'active' : ''}`} 
                                        onClick={() => {
                                            setGraphRatio({
                                                text: buttonTable.text,
                                                value: buttonTable.value
                                            })
                                        }}>
                                            {buttonTable.text}
                                    </button>
                                </TooltipHost>
                            ))}
                        </div>
                        <HighchartsReactCustom name={graphRatio.text} graphDataList={graphRatioData} />
                    </div> 
                    {isloadingSecondGraph && <Spinner size={3} style={{ padding: "1em" }} />}
                    <div className={`${BASE_CLASS}__graph__card`}>
                        <div className={`${BASE_CLASS}__buttons__graph`}>
                            {buttonsGraphList.map((buttonTable: TButtonsTable) => (
                                <TooltipHost content={buttonTable.toolTipHostContent}>
                                    <button className={`${secondGraphRatio.value === buttonTable.value ? 'active' : ''}`} 
                                        onClick={() => {
                                            setSecondGraphRatio({
                                                text: buttonTable.text,
                                                value: buttonTable.value
                                            })
                                        }}>
                                            {buttonTable.text}
                                    </button>
                                </TooltipHost>
                            ))}
                        </div>
                        <HighchartsReactCustom name={secondGraphRatio.text} graphDataList={secondGraphRatioData} />
                    </div>
                </div>
            </div>
        </div>

    )
};

export default AnalisisCuantitativo;